'use client';

import { useState, useMemo } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import { getATSKeywords, ATSKeywordSet, Keyword } from '@/lib/atsKeywordsData';
import styles from './ATSKeywords.module.css';

interface ATSKeywordsProps {
  career: CareerPath;
}

export default function ATSKeywords({ career }: ATSKeywordsProps) {
  const [activeTab, setActiveTab] = useState<'keywords' | 'samples' | 'tips'>('keywords');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Get ATS keywords for this career
  const keywords: ATSKeywordSet = useMemo(() => {
    return getATSKeywords(career);
  }, [career]);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyAllKeywords = () => {
    const allKeywords = [
      ...keywords.hardSkills.map(k => k.term),
      ...keywords.softSkills.map(k => k.term),
      ...keywords.industryTerms.map(k => k.term),
    ].join(', ');
    copyToClipboard(allKeywords, 'all-keywords');
  };

  const getImportanceClass = (importance: Keyword['importance']): string => {
    switch (importance) {
      case 'critical': return styles.importanceCritical;
      case 'high': return styles.importanceHigh;
      case 'medium': return styles.importanceMedium;
      default: return '';
    }
  };

  const tabs = [
    { id: 'keywords' as const, label: 'Keywords', icon: '🎯' },
    { id: 'samples' as const, label: 'Bullet Examples', icon: '📝' },
    { id: 'tips' as const, label: 'Resume Tips', icon: '💡' },
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>🔍</div>
        <div className={styles.headerContent}>
          <h3>ATS Resume Keywords</h3>
          <p>Optimize your resume for {career.title} positions</p>
        </div>
      </div>

      {/* Info Box */}
      <div className={styles.infoBox}>
        <span className={styles.infoIcon}>ℹ️</span>
        <div>
          <strong>What is ATS?</strong> Applicant Tracking Systems scan resumes for keywords before a human ever sees them. Include these keywords to pass the initial screening.
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabNav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Keywords Tab */}
      {activeTab === 'keywords' && (
        <div className={styles.keywordsSection}>
          {/* Legend */}
          <div className={styles.legend}>
            <span><span className={styles.importanceCritical}>●</span> Critical - Must include</span>
            <span><span className={styles.importanceHigh}>●</span> High - Strongly recommended</span>
            <span><span className={styles.importanceMedium}>●</span> Medium - Good to have</span>
          </div>

          {/* Copy All Button */}
          <button className={styles.copyAllButton} onClick={copyAllKeywords}>
            {copiedId === 'all-keywords' ? '✓ Copied All!' : '📋 Copy All Keywords'}
          </button>

          {/* Hard Skills */}
          <div className={styles.keywordGroup}>
            <h4>🔧 Technical/Hard Skills</h4>
            <div className={styles.keywordTags}>
              {keywords.hardSkills.map((skill, idx) => (
                <span
                  key={idx}
                  className={`${styles.keywordTag} ${getImportanceClass(skill.importance)}`}
                  onClick={() => copyToClipboard(skill.term, `hard-${idx}`)}
                  title={skill.context || 'Click to copy'}
                >
                  {skill.term}
                  {copiedId === `hard-${idx}` && <span className={styles.copiedBadge}>✓</span>}
                </span>
              ))}
            </div>
          </div>

          {/* Soft Skills */}
          <div className={styles.keywordGroup}>
            <h4>🤝 Soft Skills</h4>
            <div className={styles.keywordTags}>
              {keywords.softSkills.map((skill, idx) => (
                <span
                  key={idx}
                  className={`${styles.keywordTag} ${getImportanceClass(skill.importance)}`}
                  onClick={() => copyToClipboard(skill.term, `soft-${idx}`)}
                  title="Click to copy"
                >
                  {skill.term}
                  {copiedId === `soft-${idx}` && <span className={styles.copiedBadge}>✓</span>}
                </span>
              ))}
            </div>
          </div>

          {/* Industry Terms */}
          <div className={styles.keywordGroup}>
            <h4>🏢 Industry Terms</h4>
            <div className={styles.keywordTags}>
              {keywords.industryTerms.map((term, idx) => (
                <span
                  key={idx}
                  className={`${styles.keywordTag} ${getImportanceClass(term.importance)}`}
                  onClick={() => copyToClipboard(term.term, `industry-${idx}`)}
                  title={term.context || 'Click to copy'}
                >
                  {term.term}
                  {copiedId === `industry-${idx}` && <span className={styles.copiedBadge}>✓</span>}
                </span>
              ))}
            </div>
          </div>

          {/* Action Verbs */}
          <div className={styles.keywordGroup}>
            <h4>⚡ Power Action Verbs</h4>
            <p className={styles.groupIntro}>Start your bullet points with these verbs:</p>
            <div className={styles.verbTags}>
              {keywords.actionVerbs.map((verb, idx) => (
                <span
                  key={idx}
                  className={styles.verbTag}
                  onClick={() => copyToClipboard(verb, `verb-${idx}`)}
                  title="Click to copy"
                >
                  {verb}
                  {copiedId === `verb-${idx}` && <span className={styles.copiedBadge}>✓</span>}
                </span>
              ))}
            </div>
          </div>

          {/* Certifications */}
          {keywords.certifications.length > 0 && (
            <div className={styles.keywordGroup}>
              <h4>🎓 Valuable Certifications</h4>
              <div className={styles.certTags}>
                {keywords.certifications.map((cert, idx) => (
                  <div key={idx} className={`${styles.certTag} ${getImportanceClass(cert.importance)}`}>
                    <span className={styles.certName}>{cert.term}</span>
                    {cert.context && <span className={styles.certContext}>{cert.context}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sample Bullets Tab */}
      {activeTab === 'samples' && (
        <div className={styles.samplesSection}>
          <p className={styles.sectionIntro}>
            Transform vague statements into powerful, ATS-friendly bullet points:
          </p>

          {keywords.sampleBullets.map((sample, idx) => (
            <div key={idx} className={styles.sampleCard}>
              <div className={styles.sampleComparison}>
                <div className={styles.weakExample}>
                  <span className={styles.sampleLabel}>❌ Weak</span>
                  <p>{sample.weak}</p>
                </div>
                <div className={styles.sampleArrow}>→</div>
                <div className={styles.strongExample}>
                  <span className={styles.sampleLabel}>✅ Strong</span>
                  <p>{sample.strong}</p>
                  <button
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(sample.strong, `sample-${idx}`)}
                  >
                    {copiedId === `sample-${idx}` ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <div className={styles.sampleExplanation}>
                <span className={styles.whyLabel}>Why it works:</span> {sample.explanation}
              </div>
            </div>
          ))}

          {/* Formula Box */}
          <div className={styles.formulaBox}>
            <h4>The STAR Bullet Formula</h4>
            <div className={styles.formula}>
              <span className={styles.formulaItem}>
                <strong>Action Verb</strong>
                <span>+ What You Did</span>
              </span>
              <span className={styles.formulaPlus}>+</span>
              <span className={styles.formulaItem}>
                <strong>How/Tools</strong>
                <span>Used</span>
              </span>
              <span className={styles.formulaPlus}>+</span>
              <span className={styles.formulaItem}>
                <strong>Result</strong>
                <span>With Numbers</span>
              </span>
            </div>
            <p className={styles.formulaExample}>
              Example: &quot;Developed automated testing suite using Jest and Cypress, reducing bug escapes by 60% and saving 10 hours/week of manual testing.&quot;
            </p>
          </div>
        </div>
      )}

      {/* Tips Tab */}
      {activeTab === 'tips' && (
        <div className={styles.tipsSection}>
          <div className={styles.tipsList}>
            {keywords.resumeTips.map((tip, idx) => (
              <div key={idx} className={styles.tipCard}>
                <span className={styles.tipSection}>{tip.section}</span>
                <p className={styles.tipText}>{tip.tip}</p>
              </div>
            ))}
          </div>

          {/* ATS Formatting Tips */}
          <div className={styles.formatTipsBox}>
            <h4>📄 ATS-Friendly Formatting</h4>
            <ul>
              <li>Use a clean, single-column layout (no tables or columns)</li>
              <li>Stick to standard section headers: Experience, Education, Skills</li>
              <li>Use common fonts: Arial, Calibri, Georgia, Helvetica</li>
              <li>Save as .docx or PDF (check job posting for preference)</li>
              <li>Avoid headers/footers—ATS often can&apos;t read them</li>
              <li>No images, graphics, or icons</li>
              <li>Use standard bullet points (•), not fancy symbols</li>
            </ul>
          </div>

          {/* Keyword Placement */}
          <div className={styles.placementBox}>
            <h4>📍 Where to Place Keywords</h4>
            <div className={styles.placementGrid}>
              <div className={styles.placementItem}>
                <span className={styles.placementLocation}>Summary/Objective</span>
                <span className={styles.placementAdvice}>2-3 critical keywords woven naturally</span>
              </div>
              <div className={styles.placementItem}>
                <span className={styles.placementLocation}>Skills Section</span>
                <span className={styles.placementAdvice}>List all relevant hard skills</span>
              </div>
              <div className={styles.placementItem}>
                <span className={styles.placementLocation}>Experience Bullets</span>
                <span className={styles.placementAdvice}>Keywords + action verbs + metrics</span>
              </div>
              <div className={styles.placementItem}>
                <span className={styles.placementLocation}>Job Titles</span>
                <span className={styles.placementAdvice}>Match exact job title when possible</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={styles.footer}>
        <p>
          <strong>Pro Tip:</strong> For each job application, compare the job posting against these keywords and prioritize the ones that appear most frequently.
        </p>
      </div>
    </div>
  );
}
